import env from 'env';
import { ObjectValues } from 'types';
import mixpanel from 'mixpanel-browser';
import utils from 'utils';
import {
  LogoutProps,
  ErrorViewedProps,
  PageViewedProps,
  DocumentEventProps,
  MixpanelEventProps,
  SearchInitiatedEventProps,
  SecureLinkCreationInitiatedEventProps,
  ListingCreationInitiatedEventProps,
  ListingVisitedEventProps,
  MobileVerificationInitiatedEventProps,
  EnquiryCreationInitiatedEventProps,
  EnquiryWithdrawalInitiatedEventProps,
  SignupInitiatedProps,
  DealCreationInitiatedEventProps,
  SoftSignalInitiatedEventProps,
  DocumentPublishedEventProps,
  QuestionSubmissionInitiatedEventProps,
  QuestionReplyInitiatedEventProps,
  AnswerReviewInitiatedEventProps,
  QuestionReviewInitiatedEventProps,
  PhoneNumberSetupInitiatedEventProps,
  LeadNotificationsModeChangedProps,
  DataRoomTourTakenEventProps,
  DataRoomTourCardViewedEventProps,
  DataRoomTourHiddenForeverEventProps,
  PasswordForgottenProps,
} from 'models/MixpanelEvents';

const MIXPANEL_EVENT = {
  LOG_OUT: 'Log Out',
  ERROR_VIEWED: 'Error Viewed',
  PAGE_VIEWED: 'Page Viewed',
  DOCUMENT_VIEWED: 'Document Viewed',
  SEARCH_INITIATED: 'Search Initiated',
  SECURE_LINK_CREATION_INITIATED: 'Secure Link Creation Initiated',
  LISTING_CREATION_INITIATED: 'Listing Creation Initiated',
  LISTING_VISITED: 'Listing Visited',
  MOBILE_VERIFICATION_INITIATED: 'Mobile Verification Initiated',
  ENQUIRY_CREATION_INITIATED: 'Enquiry Creation Initiated',
  ENQUIRY_WITHDRAWAL_INITIATED: 'Enquiry Withdrawal Initiated',
  SIGNUP_INITIATED: 'Signup Initiated',
  DEAL_CREATION_INITIATED: 'Deal Creation Initiated',
  SOFT_SIGNAL_INITIATED: 'Soft Signal Initiated',
  DOCUMENT_PUBLISHED: 'Document Published Initiated',
  QUESTION_SUBMISSION_INITIATED: 'Question Submission Initiated',
  QUESTION_REPLY_INITIATED: 'Question Reply Initiated',
  ANSWER_REVIEW_INITIATED: 'Answer Review Initiated',
  QUESTION_REVIEW_INITIATED: 'Question Review Initiated',
  PHONE_NUMBER_SETUP_INITIATED: 'Phone Number Setup Initiated',
  LEAD_NOTIFICATIONS_MODE_CHANGED: 'Lead Notifications Mode Changed',
  DATA_ROOM_TOUR_TAKEN: 'Data Room Tour Taken',
  DATA_ROOM_TOUR_CARD_VIEWED: 'Data Room Tour Card Viewed',
  DATA_ROOM_TOUR_HIDDEN_FOREVER: 'Data Room Tour Hidden Forever',
  PASSWORD_FORGOTTEN_INITIATED: 'Password Forgotten Initiated',
} as const;

type MixpanelEvents = ObjectValues<typeof MIXPANEL_EVENT>;

const mixpanelToken = env.MIXPANEL_TOKEN;

export default {
  isInitialized: false,

  init() {
    if (!mixpanelToken || utils.isSSG()) return;

    mixpanel.init(mixpanelToken, {
      api_host: 'https://api-eu.mixpanel.com',
      record_sessions_percent: env.IS_PROD ? 100 : 0, // records 80% of all sessions on prod env
    });
    this.isInitialized = true;
  },

  getIsTrackingEnabled() {
    return this.isInitialized && !utils.isSSG();
  },

  identify(userId: string) {
    if (this.getIsTrackingEnabled()) {
      mixpanel.identify(userId);
    }
  },

  reset() {
    if (this.getIsTrackingEnabled()) {
      mixpanel.reset();
    }
  },

  trackEvent(eventName: MixpanelEvents, eventProps?: MixpanelEventProps) {
    if (this.getIsTrackingEnabled()) {
      try {
        mixpanel.track(eventName, eventProps);
      } catch {
        return;
      }
    }
  },

  logout(props: LogoutProps) {
    this.trackEvent(MIXPANEL_EVENT.LOG_OUT, props);
  },

  errorViewed(props: ErrorViewedProps) {
    this.trackEvent(MIXPANEL_EVENT.ERROR_VIEWED, props);
  },

  pageViewed(props: PageViewedProps) {
    this.trackEvent(MIXPANEL_EVENT.PAGE_VIEWED, props);
  },

  documentViewed(props: DocumentEventProps) {
    this.trackEvent(MIXPANEL_EVENT.DOCUMENT_VIEWED, props);
  },

  searchInitiated(props: SearchInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.SEARCH_INITIATED, props);
  },

  secureLinkCreationInitiated(props: SecureLinkCreationInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.SECURE_LINK_CREATION_INITIATED, props);
  },

  listingCreationInitiated(props: ListingCreationInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.LISTING_CREATION_INITIATED, props);
  },

  listingVisited(props: ListingVisitedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.LISTING_VISITED, props);
  },

  mobileVerificationInitiated(props: MobileVerificationInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.MOBILE_VERIFICATION_INITIATED, props);
  },

  enquiryCreationInitiated(props: EnquiryCreationInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.ENQUIRY_CREATION_INITIATED, props);
  },

  enquiryWithdrawalInitiated(props: EnquiryWithdrawalInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.ENQUIRY_WITHDRAWAL_INITIATED, props);
  },

  signupInitiated(props: SignupInitiatedProps) {
    this.trackEvent(MIXPANEL_EVENT.SIGNUP_INITIATED, props);
  },

  dealRoomCreationInitiated(props: DealCreationInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.DEAL_CREATION_INITIATED, props);
  },

  softSignalInitiated(props: SoftSignalInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.SOFT_SIGNAL_INITIATED, props);
  },

  documentPublished(props: DocumentPublishedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.DOCUMENT_PUBLISHED, props);
  },

  questionSubmissionInitiated(props: QuestionSubmissionInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.QUESTION_SUBMISSION_INITIATED, props);
  },

  questionReplyInitiated(props: QuestionReplyInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.QUESTION_REPLY_INITIATED, props);
  },

  answerReviewInitiated(props: AnswerReviewInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.ANSWER_REVIEW_INITIATED, props);
  },

  questionReviewInitiated(props: QuestionReviewInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.QUESTION_REVIEW_INITIATED, props);
  },

  phoneNumberSetupInitiated(props: PhoneNumberSetupInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.PHONE_NUMBER_SETUP_INITIATED, props);
  },

  leadNotificationsModeChanged(props: LeadNotificationsModeChangedProps) {
    this.trackEvent(MIXPANEL_EVENT.LEAD_NOTIFICATIONS_MODE_CHANGED, props);
  },

  dataRoomTourTaken(props: DataRoomTourTakenEventProps) {
    this.trackEvent(MIXPANEL_EVENT.DATA_ROOM_TOUR_TAKEN, props);
  },

  dataRoomTourCardViewed(props: DataRoomTourCardViewedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.DATA_ROOM_TOUR_CARD_VIEWED, props);
  },

  dataRoomTourHiddenForever(props: DataRoomTourHiddenForeverEventProps) {
    this.trackEvent(MIXPANEL_EVENT.DATA_ROOM_TOUR_HIDDEN_FOREVER, props);
  },

  passwordForgottenInitiated(props: PasswordForgottenProps) {
    this.trackEvent(MIXPANEL_EVENT.PASSWORD_FORGOTTEN_INITIATED, props);
  },
};
